import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TCreateGroupBody, TGroupDetail, TPlacement, TUpdateGroupBody } from 'types/story';

import { storyKeys } from './story.queries';
import {
  activateGroup,
  createGroup,
  deleteGroup,
  disableGroup,
  getGroup,
  getGroups,
  publishGroup,
  updateGroup,
} from './story.requests';

export const DEFAULT_PLACEMENT: TPlacement = 'HOME';
const DEFAULT_PAGE_SIZE = 20;

export const useGetGroups = (
  placement: TPlacement = DEFAULT_PLACEMENT,
  size: number = DEFAULT_PAGE_SIZE
) => {
  const query = useInfiniteQuery({
    queryKey: storyKeys.groups(placement),
    queryFn: ({ pageParam }) => getGroups({ placement, page: pageParam, size }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const isLast = lastPage.isLastPage ?? lastPage.content.length < size;
      return isLast ? undefined : lastPage.page + 1;
    },
  });

  const groups: TGroupDetail[] = query.data?.pages.flatMap((page) => page.content) ?? [];

  return {
    groups,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};

export const useGetGroup = (id: string | undefined) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: storyKeys.group(id ?? ''),
    queryFn: () => getGroup(id as string),
    enabled: !!id,
  });

  return { group: data, isLoading, isError, refetch };
};

const useCacheGroup = () => {
  const queryClient = useQueryClient();

  return (group: TGroupDetail) => {
    queryClient.setQueryData(storyKeys.group(group.id), group);
    queryClient.invalidateQueries({ queryKey: storyKeys.groups(group.placement) });
  };
};

export const useCreateGroup = () => {
  const cacheGroup = useCacheGroup();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: TCreateGroupBody) => createGroup(body),
    onSuccess: cacheGroup,
  });

  return { createGroup: mutateAsync, isPending };
};

export const useUpdateGroup = () => {
  const cacheGroup = useCacheGroup();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, body }: { id: string; body: TUpdateGroupBody }) => updateGroup(id, body),
    onSuccess: cacheGroup,
  });

  return { updateGroup: mutateAsync, isPending };
};

export const usePublishGroup = () => {
  const cacheGroup = useCacheGroup();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => publishGroup(id),
    onSuccess: cacheGroup,
  });

  return { publishGroup: mutateAsync, isPending };
};

export const useDisableGroup = () => {
  const cacheGroup = useCacheGroup();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => disableGroup(id),
    onSuccess: cacheGroup,
  });

  return { disableGroup: mutateAsync, isPending };
};

export const useActivateGroup = () => {
  const cacheGroup = useCacheGroup();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => activateGroup(id),
    onSuccess: cacheGroup,
  });

  return { activateGroup: mutateAsync, isPending };
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: storyKeys.group(id) });
      queryClient.invalidateQueries({ queryKey: storyKeys.all });
    },
  });

  return { deleteGroup: mutateAsync, isPending };
};
